/**
 * In-app purchases (Google Play Billing / StoreKit 2) via @capgo/native-purchases.
 * The app only obtains the platform receipt; coins are credited exclusively after server-side verification
 * (`POST /api/v1/store/purchases/verify`). The user id (a UUID) is passed as appAccountToken / obfuscated
 * account id so receipts can't be redeemed by another account.
 */
import type { ProductDto } from '@cardhub/protocol';
import { api } from '../api/client';
import { uuid } from '../lib/device';
import { isNative, platform } from './platform';

export interface StoreProduct extends ProductDto {
  platformSku: string;
  localizedPrice: string | null;
}

export const purchasesSupported = isNative;

export function platformSku(p: ProductDto & { platformSkus?: Record<string, string> }): string {
  const key = platform === 'ios' ? 'app_store' : 'google_play';
  return p.platformSkus?.[key] ?? p.sku;
}

/** Adds localized store prices to server products (falls back to the server price). */
export async function loadLocalizedPrices(products: (ProductDto & { platformSkus?: Record<string, string> })[]): Promise<StoreProduct[]> {
  const base = products.map((p) => ({ ...p, platformSku: platformSku(p), localizedPrice: null as string | null }));
  if (!isNative) return base;
  try {
    const { NativePurchases, PURCHASE_TYPE } = await import('@capgo/native-purchases');
    const { products: native } = await NativePurchases.getProducts({ productIdentifiers: base.map((b) => b.platformSku), productType: PURCHASE_TYPE.INAPP });
    const prices = new Map(native.map((n) => [n.identifier, n.priceString]));
    return base.map((b) => ({ ...b, localizedPrice: prices.get(b.platformSku) ?? null }));
  } catch {
    return base;
  }
}

/** Runs the native purchase sheet, then has the server verify the receipt and credit coins. */
export async function buyNative(product: StoreProduct, userId: string): Promise<{ coins: number; balance: number }> {
  const { NativePurchases, PURCHASE_TYPE } = await import('@capgo/native-purchases');
  const tx = await NativePurchases.purchaseProduct({
    productIdentifier: product.platformSku,
    productType: PURCHASE_TYPE.INAPP,
    appAccountToken: userId,
    isConsumable: false, // consumption is done server-side after verification
  });
  const receipt =
    platform === 'ios'
      ? (tx.jwsRepresentation ?? '')
      : JSON.stringify({ productId: tx.productIdentifier, purchaseToken: tx.purchaseToken });
  if (!receipt) throw new Error('Store did not return a receipt');
  return api.post<{ coins: number; balance: number }>('/api/v1/store/purchases/verify', {
    platform: platform === 'ios' ? 'app_store' : 'google_play',
    productId: product.id,
    receipt,
    idempotencyKey: tx.transactionId ? `tx-${tx.transactionId}` : uuid(),
  });
}
